// ============================================================
// DIGITAL TWIN ENGINE — Fase 10.1 + Fase 11 (Hardened)
// Motor principal do digital twin operacional
// FIX: addCorrelations→addCorrelation, register equipment getter,
//      warm cooldowns from DB, risk score excludes propagated
// ============================================================
import { loadTwinState, subscribeTwinRealtime, takeTwinSnapshot } from './twinStateManager'
import { useTwinStore } from '@/store/twinStore'
import { obs } from '@/core/observability'
import {
  runAutonomousDecisions,
  warmCooldownsFromDB,
  registerEquipmentGetter,
} from '@/core/autonomous/autonomousDecisionEngine'
import { detectCorrelations } from '@/core/analytics/correlationEngine'
import { getCriticalEquipment } from '@/core/digital-twin/equipmentTracker'

// Register equipment getter at module level — eliminates require() in autonomous engine
registerEquipmentGetter(getCriticalEquipment)

interface EngineInstance {
  unsubscribeRealtime: () => void
  pollingTimer:        ReturnType<typeof setInterval>
  snapshotTimer:       ReturnType<typeof setInterval>
}

const instances = new Map<string, EngineInstance>()

// ─── Start engine for an obra ─────────────────────────────────
export async function startDigitalTwinEngine(
  obraId:    string,
  companyId: string,
  opts: {
    pollIntervalMs?:     number   // default 60s
    snapshotIntervalMs?: number   // default 5min
  } = {},
): Promise<() => void> {
  stopDigitalTwinEngine(obraId)

  const {
    pollIntervalMs     = 60_000,
    snapshotIntervalMs = 300_000,
  } = opts

  obs.info('twin_engine', `Starting Digital Twin for obra ${obraId}`)

  // Initial load
  await refreshTwinState(obraId)

  // FIX PROB-04: Warm cooldown state from DB to prevent post-restart duplicates
  await warmCooldownsFromDB(obraId)

  // Subscribe to realtime
  const unsubscribeRealtime = subscribeTwinRealtime(obraId)

  // Polling for autonomous decisions + correlations
  const pollingTimer = setInterval(() => {
    void refreshTwinState(obraId)
      .then(() => runAutonomousDecisions(obraId, companyId))
      .then(() => detectAndPublishCorrelations(obraId))
      .catch((err: unknown) => {
        obs.error('twin_engine', `Poll cycle failed: ${String(err)}`, { obraId })
      })
  }, pollIntervalMs)

  // Snapshot timer
  const snapshotTimer = setInterval(() => {
    const state = useTwinStore.getState()
    if (state.twinState) {
      const riskScore = computeOverallRiskScore(state)
      void takeTwinSnapshot(obraId, state.twinState, riskScore)
        .catch((err: unknown) => {
          obs.warn('twin_engine', `Snapshot failed: ${String(err)}`, { obraId })
        })
    }
  }, snapshotIntervalMs)

  const instance: EngineInstance = { unsubscribeRealtime, pollingTimer, snapshotTimer }
  instances.set(obraId, instance)

  obs.info('twin_engine', `Engine running for obra ${obraId}`)
  return () => stopDigitalTwinEngine(obraId)
}

export function stopDigitalTwinEngine(obraId: string): void {
  const inst = instances.get(obraId)
  if (!inst) return
  inst.unsubscribeRealtime()
  clearInterval(inst.pollingTimer)
  clearInterval(inst.snapshotTimer)
  instances.delete(obraId)

  // FIX FASE 13.6: Store isolation — reset twin state when engine stops
  // Prevents stale obra data bleeding into a new obra session
  useTwinStore.getState().reset()

  obs.info('twin_engine', `Engine stopped and store reset for obra ${obraId}`)
}

// ─── Refresh ──────────────────────────────────────────────────
async function refreshTwinState(obraId: string): Promise<void> {
  const store = useTwinStore.getState()
  store.setLoading(true)

  try {
    const state = await loadTwinState(obraId)
    if (state) {
      store.setTwinState(state)
      store.setOverallRiskScore(computeOverallRiskScore(store))
    }
  } catch (err) {
    obs.error('twin_engine', `Refresh failed: ${String(err)}`, { obraId })
    store.setError(String(err))
  } finally {
    useTwinStore.getState().setLoading(false)
  }
}

// ─── Overall risk score ───────────────────────────────────────
// FIX PROB-12: Exclude propagated risks from score computation
function computeOverallRiskScore(store: ReturnType<typeof useTwinStore.getState>): number {
  const state = store.twinState
  if (!state) return 0

  // Only count original (non-propagated) active risks
  const activeRisks = state.risks.filter(r => r.is_active && !r.is_propagated)
  if (activeRisks.length === 0) return 0

  const emergencies = activeRisks.filter(r => r.severity === 'emergency').length
  const criticals   = activeRisks.filter(r => r.severity === 'critical').length
  const warnings    = activeRisks.filter(r => r.severity === 'warning').length

  return Math.min(100, emergencies * 30 + criticals * 15 + warnings * 5)
}

// ─── Correlations ────────────────────────────────────────────
async function detectAndPublishCorrelations(obraId: string): Promise<void> {
  const store = useTwinStore.getState()
  if (!store.twinState) return

  const correlations = detectCorrelations({
    obraId,
    spatialRisks:  store.twinState.risks,
    equipment:     store.twinState.equipment,
    correlations:  store.twinState.correlations,
  })

  if (correlations.length > 0) {
    // FIX PROB-02: addCorrelation (singular) — was addCorrelations (didn't exist)
    correlations.forEach(c => store.addCorrelation(c))
  }
}
